import { AnalyticsComponentName } from '@app/pages/i/analytics';
import { UserInfo } from '@redbubble/next/types';
import {
  type Item as AnalyticsEcommerceItem,
  AnalyticsItemsList,
} from '@redbubble/next-analytics/gtm';
import { useUserInfo } from '@redbubble/next-provider';
import {
  RSPageType,
  RSProductClickedLocation,
} from '@redbubble/next-provider/rudderstack';
import { useEffect, useMemo } from 'react';
import { CarouselSection } from '../../../../components/CarouselSection';
import {
  CarouselSectionContext,
  CarouselSectionContextValue,
} from '../../../../components/CarouselSection/carouselSectionContext';
import { useListsInclusions } from '../../../../hooks/useListsInclusions';
import { SearchUUIDStorageKey } from '../../../../utils/searchUUIDStorage';
import {
  parseRecommendedInventoryItemToAnalyticsEcommerceItem,
  trackGA4ViewItemListEvent,
} from '../../analytics';
import { PreviewStyle } from '../../constants';
import { useCurrentInventoryStore } from '../../hooks/useCurrentInventoryItem';
import { LicensedProductsSectionHeader } from './LicensedProductsSectionHeader';
import { useLicensedProducts } from './useLicensedProducts';
import { useLicensingMarks } from './useLicensingMark';
import {
  mapLicensedProductMediaCardProps,
  mapLicensedProductPriceProps,
} from './utils';

const PRODUCT_LIMIT = 32;

export interface LicensedProductsSectionProps {
  workId: number;
  artistUsername: string;
}

export const LicensedProductsSection = ({
  workId,
  artistUsername,
}: LicensedProductsSectionProps) => {
  const [userInfo] = useUserInfo<UserInfo>();
  const id = useCurrentInventoryStore((state) => state.currentInventoryItem.id);
  const {
    refresh: refreshLicensingMarks,
    loading: loadingLicensingMarks,
    licensingMarks,
  } = useLicensingMarks();

  const hasNoLicensingMarks = !licensingMarks.find(
    (mark) => mark.imageSrc && mark.legalLine
  );

  const {
    refresh: refreshLicensedProducts,
    loading: loadingLicensedProducts,
    licensedProducts,
  } = useLicensedProducts();

  useEffect(() => {
    refreshLicensingMarks({
      workId,
    });
  }, [refreshLicensingMarks, workId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
  useEffect(() => {
    if (!loadingLicensingMarks && !hasNoLicensingMarks) {
      const { locale, currency, country } = userInfo;

      refreshLicensedProducts({
        inventoryItemId: id,
        artistUsername,
        limit: PRODUCT_LIMIT,
        currency,
        country,
        locale,
        previewTypeIds: [
          PreviewStyle.CLOSE_PREVIEW,
          PreviewStyle.ALTERNATE_PRODUCT_CLOSE,
        ],
        famProperty: licensingMarks.map((mark) => mark.agreementId).join('|'),
      });
    }
  }, [
    artistUsername,
    refreshLicensedProducts,
    userInfo,
    licensingMarks,
    loadingLicensingMarks,
    hasNoLicensingMarks,
  ]);

  const { listsInclusions, refresh: refreshListInclusions } =
    useListsInclusions();

  useEffect(() => {
    if (!loadingLicensedProducts)
      refreshListInclusions(licensedProducts.map((lp) => lp?.inventoryItem));
  }, [loadingLicensedProducts, licensedProducts, refreshListInclusions]);

  const contextValue: CarouselSectionContextValue = useMemo(() => {
    return {
      mediaCardPropsMapper: mapLicensedProductMediaCardProps(listsInclusions),
      pricePropsMapper: mapLicensedProductPriceProps,
      analytics: {
        componentName: AnalyticsComponentName.LicensedProduct,
        listID: AnalyticsItemsList.LicensedProductsCarousel,
        inViewTracking: () => {
          const analyticsItems = licensedProducts
            .map((item, index) => {
              if (!item) return null;

              return parseRecommendedInventoryItemToAnalyticsEcommerceItem({
                recommendedInventoryItem: item,
                index,
              });
            })
            .filter(
              (
                item: AnalyticsEcommerceItem | null
              ): item is NonNullable<AnalyticsEcommerceItem> => !!item
            );

          trackGA4ViewItemListEvent({
            items: analyticsItems,
            listID: AnalyticsItemsList.LicensedProductsCarousel,
            topic: '',
            searchUUIDStorageKey: SearchUUIDStorageKey.LocationPathname,
          });
        },
      },
      rsLocation: RSProductClickedLocation.LicensedProductsCarousel,
      rsPageType: RSPageType.Product,
    };
  }, [licensedProducts, listsInclusions]);

  if (!loadingLicensingMarks && hasNoLicensingMarks) return null;

  return (
    <CarouselSectionContext.Provider value={contextValue}>
      <CarouselSection.Container>
        <LicensedProductsSectionHeader
          licenseMarks={licensingMarks}
          loading={loadingLicensingMarks}
        />
        <CarouselSection.Carousel>
          {licensedProducts.map((product, index) => {
            return (
              <CarouselSection.CarouselSlide
                key={`licensed-product-slide-${product?.inventoryItemId}`}
              >
                <CarouselSection.CarouselMediaCard
                  data={product}
                  index={index}
                />
              </CarouselSection.CarouselSlide>
            );
          })}
        </CarouselSection.Carousel>
      </CarouselSection.Container>
    </CarouselSectionContext.Provider>
  );
};
